import React, { useState, useEffect, useCallback, useMemo } from "react";
import { apiRequests } from "../../shared/api/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/components/ui/tabs";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { Skeleton } from "src/components/ui/skeleton";
import { Badge } from "src/components/ui/badge";
import { Switch } from "src/components/ui/switch";
import { Progress } from "src/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import {
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Line,
  Bar,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import {
  Server,
  Database,
  Cpu,
  HardDrive,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Activity,
  List,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
} from "lucide-react";
import { toast } from "sonner";

const COLORS = {
  cpu: "#3b82f6",
  memory: "#10b981",
  disk: "#f59e0b",
  error5xx: "#ef4444",
  error4xx: "#f97316",
};

// ---------- Компонент сортируемой таблицы ----------
const SortableTable = ({ columns, data, defaultSort, loading }) => {
  const [sortConfig, setSortConfig] = useState(
    defaultSort || { key: null, direction: "asc" },
  );

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  if (loading) return <Skeleton className="h-64" />;
  if (!data.length) return <EmptyState>Нет данных</EmptyState>;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((col) => (
            <TableHead
              key={col.key}
              className="cursor-pointer select-none"
              onClick={() => requestSort(col.key)}
            >
              <div className="flex items-center gap-1">
                {col.label}
                {sortConfig.key === col.key ? (
                  sortConfig.direction === "asc" ? (
                    <ArrowUpDown className="h-4 w-4" />
                  ) : (
                    <ArrowUpDown className="h-4 w-4 rotate-180" />
                  )
                ) : (
                  <ArrowUpDown className="h-4 w-4 opacity-30" />
                )}
              </div>
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedData.map((row, idx) => (
          <TableRow key={idx}>
            {columns.map((col) => (
              <TableCell key={col.key}>
                {col.render ? col.render(row) : row[col.key]}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

// ---------- График ресурсов с модалкой процессов ----------
const ResourceChart = ({ data, username, password }) => {
  const [showModal, setShowModal] = useState(false);
  const [processes, setProcesses] = useState([]);
  const [loadingProc, setLoadingProc] = useState(false);

  const handleClick = async () => {
    setShowModal(true);
    setLoadingProc(true);
    try {
      const res = await apiRequests.loggerinfo.getTopProcesses(
        username,
        password,
        20,
      );
      setProcesses(res.processes || []);
    } catch {
      toast.error("Не удалось загрузить процессы");
    } finally {
      setLoadingProc(false);
    }
  };

  return (
    <>
      <Card
        className="cursor-pointer hover:shadow-md transition"
        onClick={handleClick}
      >
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            <span>Использование ресурсов за час</span>
            <Button variant="ghost" size="sm">
              <List className="h-4 w-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" tick={{ fontSize: 10 }} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area
                type="monotone"
                dataKey="cpu"
                stroke={COLORS.cpu}
                fill={COLORS.cpu}
                fillOpacity={0.2}
                name="CPU %"
              />
              <Area
                type="monotone"
                dataKey="memory"
                stroke={COLORS.memory}
                fill={COLORS.memory}
                fillOpacity={0.2}
                name="RAM %"
              />
              <Area
                type="monotone"
                dataKey="disk"
                stroke={COLORS.disk}
                fill={COLORS.disk}
                fillOpacity={0.2}
                name="Disk %"
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Топ процессов по потреблению</DialogTitle>
          </DialogHeader>
          {loadingProc ? (
            <Skeleton className="h-64" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PID</TableHead>
                  <TableHead>Имя</TableHead>
                  <TableHead>CPU %</TableHead>
                  <TableHead>RAM %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {processes.map((p) => (
                  <TableRow key={p.pid}>
                    <TableCell>{p.pid}</TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{p.cpu_percent?.toFixed(1)}</TableCell>
                    <TableCell>{p.memory_percent?.toFixed(1)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// ---------- Карточка ошибки с классификацией ----------
const ErrorCard = ({ error, expanded, onToggle }) => {
  const is5xx = error.status_code >= 500;
  const is4xx = error.status_code >= 400 && error.status_code < 500;
  const badgeColor = is5xx ? "destructive" : is4xx ? "secondary" : "outline";

  return (
    <Card
      className={`border-l-4 ${is5xx ? "border-l-red-500" : is4xx ? "border-l-amber-500" : "border-l-gray-300"}`}
    >
      <CardContent className="p-4">
        <div
          className="flex justify-between items-start cursor-pointer"
          onClick={onToggle}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={badgeColor}>{error.status_code}</Badge>
            <span className="font-mono text-sm">{error.method}</span>
            <span className="text-sm text-muted-foreground truncate max-w-lg">
              {error.url}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {new Date(error.created_at).toLocaleString()}
            </span>
            {expanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </div>
        </div>
        {expanded && (
          <div className="mt-3 space-y-2">
            {error.traceback && (
              <pre className="p-2 bg-gray-100 rounded text-xs overflow-x-auto max-h-60 whitespace-pre-wrap">
                {error.traceback}
              </pre>
            )}
            <div className="text-xs text-muted-foreground">
              IP: {error.ip || "—"} | Cashbox: {error.cashbox_id || "—"}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---------- Группировка ошибок по папкам ----------
const ErrorFolders = ({ errors, onSelectError }) => {
  const [selectedType, setSelectedType] = useState("all");
  const filtered = useMemo(() => {
    if (selectedType === "all") return errors;
    if (selectedType === "5xx")
      return errors.filter((e) => e.status_code >= 500);
    return errors.filter((e) => e.status_code >= 400 && e.status_code < 500);
  }, [errors, selectedType]);

  const counts = useMemo(
    () => ({
      all: errors.length,
      "5xx": errors.filter((e) => e.status_code >= 500).length,
      "4xx": errors.filter((e) => e.status_code >= 400 && e.status_code < 500)
        .length,
    }),
    [errors],
  );

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button
          variant={selectedType === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setSelectedType("all")}
        >
          Все ({counts.all})
        </Button>
        <Button
          variant={selectedType === "5xx" ? "destructive" : "outline"}
          size="sm"
          onClick={() => setSelectedType("5xx")}
        >
          5xx ({counts["5xx"]})
        </Button>
        <Button
          variant={selectedType === "4xx" ? "secondary" : "outline"}
          size="sm"
          onClick={() => setSelectedType("4xx")}
        >
          4xx ({counts["4xx"]})
        </Button>
      </div>
      <div className="space-y-3">
        {filtered.map((err) => (
          <ErrorCard
            key={err.id}
            error={err}
            expanded={false}
            onToggle={() => onSelectError(err)}
          />
        ))}
      </div>
    </div>
  );
};

// ---------- Хедер дашборда ----------
const Header = ({ status, autoRefresh, setAutoRefresh, onRefresh }) => (
  <div className="flex items-center justify-between flex-wrap gap-4">
    <h1 className="text-2xl font-bold flex items-center gap-2">
      <Activity className="h-6 w-6 text-blue-600" />
      Панель наблюдаемости
      <Badge variant="outline" className="ml-2">
        {new Date(status.timestamp).toLocaleString()}
      </Badge>
    </h1>
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2">
        <Switch
          id="auto-refresh"
          checked={autoRefresh}
          onCheckedChange={setAutoRefresh}
        />
        <Label htmlFor="auto-refresh" className="text-sm">
          Автообновление (30с)
        </Label>
      </div>
      <Button variant="outline" size="sm" onClick={onRefresh}>
        <RefreshCw className="h-4 w-4 mr-2" />
        Обновить
      </Button>
    </div>
  </div>
);

// ---------- Компонент метрики ----------
const MetricCard = ({ icon: Icon, title, value, sub, progress }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-medium flex items-center gap-2">
        <Icon className="h-4 w-4" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      {progress !== undefined && (
        <Progress value={progress} className="mt-2 h-1.5" />
      )}
    </CardContent>
  </Card>
);

const ServiceStatus = ({ name, ok, status }) => (
  <div className="flex items-center gap-2">
    {ok ? (
      <CheckCircle2 className="h-5 w-5 text-green-600" />
    ) : (
      <XCircle className="h-5 w-5 text-red-600" />
    )}
    <span>
      {name}: {status}
    </span>
  </div>
);

// ---------- Карточка медленного запроса ----------
const SlowQueryCard = ({ query }) => (
  <Card className="mb-3">
    <CardContent className="p-4">
      <div className="flex justify-between">
        <Badge variant="outline">
          PID {query.pid} — {query.duration_sec} сек
        </Badge>
        <span className="text-sm">{query.usename}</span>
      </div>
      <pre className="bg-gray-900 text-gray-100 p-3 rounded text-xs overflow-x-auto mt-2">
        {query.query}
      </pre>
    </CardContent>
  </Card>
);

// ---------- Вкладка ежедневного отчёта ----------
const DailyReportTab = ({
  report,
  reportDate,
  setReportDate,
  loading,
  onFetch,
}) => {
  const handleFetch = () => onFetch(reportDate || null);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Ежедневный отчёт</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-4 items-end">
          <div>
            <Label>Дата</Label>
            <Input
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
            />
          </div>
          <Button onClick={handleFetch} disabled={loading}>
            Загрузить
          </Button>
        </div>
        {loading ? (
          <Skeleton className="h-64" />
        ) : report ? (
          <DailyReportContent report={report} />
        ) : (
          <EmptyState>Нет данных</EmptyState>
        )}
      </CardContent>
    </Card>
  );
};

const DailyReportContent = ({ report }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <StatCard label="Всего запросов" value={report.summary?.total_requests} />
      <StatCard
        label="Ошибок 5xx"
        value={report.summary?.errors_5xx}
        color="red"
      />
      <StatCard
        label="Среднее время"
        value={`${report.summary?.avg_response_ms} мс`}
      />
      <StatCard
        label="Медленных (>2с)"
        value={report.summary?.slow_requests_gt2s}
        color="amber"
      />
    </div>
    <div>
      <h3 className="font-semibold mb-2">Топ медленных API</h3>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Путь</TableHead>
            <TableHead>p95 (мс)</TableHead>
            <TableHead>Количество</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {report.top_slow_api?.map((api, i) => (
            <TableRow key={i}>
              <TableCell>{api.path}</TableCell>
              <TableCell>{api.p95_ms}</TableCell>
              <TableCell>{api.count}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
);

const StatCard = ({ label, value, color = "default" }) => (
  <Card>
    <CardHeader className="p-4">
      <CardTitle className="text-sm">{label}</CardTitle>
    </CardHeader>
    <CardContent className="p-4 pt-0">
      <span
        className={`text-2xl font-bold ${color === "red" ? "text-red-600" : color === "amber" ? "text-amber-600" : ""}`}
      >
        {value}
      </span>
    </CardContent>
  </Card>
);

// ---------- Таблица лога медленных запросов ----------
const SlowLogTable = ({ data }) => {
  const columns = [
    {
      key: "ts",
      label: "Время",
      render: (row) => new Date(row.ts).toLocaleString(),
    },
    {
      key: "path",
      label: "Путь",
      render: (row) => <span className="font-mono text-xs">{row.path}</span>,
    },
    { key: "ms", label: "Длительность (мс)" },
  ];
  return (
    <SortableTable
      columns={columns}
      data={data}
      defaultSort={{ key: "ms", direction: "desc" }}
      loading={false}
    />
  );
};

// ---------- Детали ошибки ----------
const ErrorDetail = ({ error }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-2 text-sm">
      <div>
        <span className="font-medium">Статус:</span> {error.status_code}
      </div>
      <div>
        <span className="font-medium">Метод:</span> {error.method}
      </div>
      <div className="col-span-2">
        <span className="font-medium">URL:</span>{" "}
        <span className="font-mono">{error.url}</span>
      </div>
      <div>
        <span className="font-medium">Время:</span>{" "}
        {new Date(error.created_at).toLocaleString()}
      </div>
      <div>
        <span className="font-medium">IP:</span> {error.ip || "—"}
      </div>
      <div className="col-span-2">
        <span className="font-medium">Cashbox ID:</span>{" "}
        {error.cashbox_id || "—"}
      </div>
    </div>
    {error.traceback && (
      <div>
        <h4 className="font-medium mb-2">Стек ошибки:</h4>
        <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-96 whitespace-pre-wrap">
          {error.traceback}
        </pre>
      </div>
    )}
  </div>
);

const EmptyState = ({ children }) => (
  <div className="text-center py-10 text-muted-foreground">{children}</div>
);

const LoadingSkeleton = () => (
  <div className="p-8 space-y-4">
    <Skeleton className="h-8 w-1/3" />
    <div className="grid grid-cols-3 gap-4">
      <Skeleton className="h-32" />
      <Skeleton className="h-32" />
      <Skeleton className="h-32" />
    </div>
    <Skeleton className="h-64" />
  </div>
);

// ---------- Основной дашборд ----------
const Dashboard = ({ username, password }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [status, setStatus] = useState(null);
  const [hourlyStats, setHourlyStats] = useState([]);
  const [tables, setTables] = useState([]);
  const [errors, setErrors] = useState([]);
  const [slowQueries, setSlowQueries] = useState([]);
  const [dailyReport, setDailyReport] = useState(null);
  const [reportDate, setReportDate] = useState("");
  const [slowRequestsLog, setSlowRequestsLog] = useState([]);
  const [resourceHistory, setResourceHistory] = useState([]);
  const [selectedError, setSelectedError] = useState(null);
  const [loading, setLoading] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading((p) => ({
      ...p,
      status: true,
      hourly: true,
      tables: true,
      errors: true,
      slow: true,
      resources: true,
    }));
    try {
      const [
        statusData,
        hourlyData,
        tablesData,
        errorsData,
        slowData,
        resourcesData,
      ] = await Promise.all([
        apiRequests.loggerinfo.getStatus(username, password).catch((e) => null),
        apiRequests.loggerinfo
          .getHourlyStats(username, password, 24)
          .catch((e) => null),
        apiRequests.loggerinfo
          .getTables(username, password, 50)
          .catch((e) => null),
        apiRequests.loggerinfo
          .getErrors(username, password, 48, 500)
          .catch((e) => null),
        apiRequests.loggerinfo
          .getSlowQueries(username, password, 2)
          .catch((e) => null),
        apiRequests.loggerinfo
          .getResourceHistory(username, password, 60)
          .then((resp) => resp.history || [])
          .catch(() => []),
      ]);
      setStatus(statusData);
      setHourlyStats(hourlyData?.hourly_stats || []);
      setTables(tablesData?.tables || []);
      setErrors(errorsData?.errors || []);
      setSlowQueries(slowData?.slow_queries || []);
      setResourceHistory(resourcesData);
    } finally {
      setLoading((p) => ({
        ...p,
        status: false,
        hourly: false,
        tables: false,
        errors: false,
        slow: false,
        resources: false,
      }));
    }
  }, [username, password]);

  const fetchSlowLog = useCallback(async () => {
    setLoading((p) => ({ ...p, log: true }));
    try {
      const data = await apiRequests.loggerinfo.getSlowRequestsLog(
        username,
        password,
        200,
      );
      setSlowRequestsLog(data.slow_requests || []);
    } catch {
      toast.error("Ошибка загрузки лога");
    } finally {
      setLoading((p) => ({ ...p, log: false }));
    }
  }, [username, password]);

  useEffect(() => {
    fetchAll();
    fetchSlowLog();
  }, [fetchAll, fetchSlowLog]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchAll]);

  if (!status) return <LoadingSkeleton />;

  const columnsTables = [
    {
      key: "relname",
      label: "Таблица",
      render: (row) => <span className="font-mono">{row.relname}</span>,
    },
    {
      key: "rows",
      label: "Строк",
      render: (row) => row.rows?.toLocaleString(),
    },
    {
      key: "seq_scan",
      label: "Seq Scan",
      render: (row) => row.seq_scan?.toLocaleString(),
    },
    {
      key: "idx_scan",
      label: "Idx Scan",
      render: (row) => row.idx_scan?.toLocaleString(),
    },
    { key: "total_size", label: "Размер" },
    { key: "index_size", label: "Индексы" },
  ];

  return (
    <div className="p-6 space-y-6 max-w-full">
      <Header
        status={status}
        autoRefresh={autoRefresh}
        setAutoRefresh={setAutoRefresh}
        onRefresh={fetchAll}
      />
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="overview">Обзор</TabsTrigger>
          <TabsTrigger value="database">База данных</TabsTrigger>
          <TabsTrigger value="errors">Ошибки</TabsTrigger>
          <TabsTrigger value="slow">Медленные запросы</TabsTrigger>
          <TabsTrigger value="daily">Ежедневный отчёт</TabsTrigger>
          <TabsTrigger value="log">Лог медленных</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <MetricCard
              icon={Cpu}
              title="CPU"
              value={`${status.system.cpu_percent}%`}
              progress={status.system.cpu_percent}
            />
            <MetricCard
              icon={Server}
              title="Память"
              value={`${status.system.memory.percent}%`}
              sub={`${status.system.memory.used_gb}/${status.system.memory.total_gb} GB`}
              progress={status.system.memory.percent}
            />
            <MetricCard
              icon={HardDrive}
              title="Диск"
              value={`${status.system.disk.percent}%`}
              sub={`${status.system.disk.used_gb}/${status.system.disk.total_gb} GB`}
              progress={status.system.disk.percent}
            />
            <MetricCard
              icon={Database}
              title="PostgreSQL"
              value={`${status.postgres.slow_queries_active} медл.`}
            />
          </div>

          {/* Графики в один ряд */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ResourceChart
              data={resourceHistory}
              username={username}
              password={password}
              height={200}
            />
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                  Почасовая нагрузка (24ч)
                </CardTitle>
              </CardHeader>
              <CardContent className="h-[200px]">
                {hourlyStats.length === 0 ? (
                  <EmptyState>Нет данных</EmptyState>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={hourlyStats}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Bar
                        yAxisId="left"
                        dataKey="requests"
                        fill="#3b82f6"
                        name="Запросы"
                      />
                      <Bar
                        yAxisId="right"
                        dataKey="errors_5xx"
                        fill={COLORS.error5xx}
                        name="Ошибки 5xx"
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="avg_ms"
                        stroke="#10b981"
                        name="Среднее (мс)"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Интеграции</CardTitle>
              </CardHeader>
              <CardContent className="flex gap-6">
                <ServiceStatus
                  name="Redis"
                  ok={status.integrations.redis.ok}
                  status={status.integrations.redis.status}
                />
                <ServiceStatus
                  name="RabbitMQ"
                  ok={status.integrations.rabbitmq.ok}
                  status={status.integrations.rabbitmq.status}
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="database">
          <Card>
            <CardHeader>
              <CardTitle>Таблицы PostgreSQL</CardTitle>
            </CardHeader>
            <CardContent>
              <SortableTable
                columns={columnsTables}
                data={tables}
                loading={loading.tables}
                defaultSort={{ key: "total_size", direction: "desc" }}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="errors">
          <Card>
            <CardHeader>
              <CardTitle>Ошибки за 48 часов</CardTitle>
            </CardHeader>
            <CardContent>
              {loading.errors ? (
                <Skeleton className="h-96" />
              ) : (
                <ErrorFolders
                  errors={errors}
                  onSelectError={setSelectedError}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="slow">
          <Card>
            <CardHeader>
              <CardTitle>Активные медленные запросы</CardTitle>
            </CardHeader>
            <CardContent>
              {loading.slow ? (
                <Skeleton className="h-96" />
              ) : slowQueries.length === 0 ? (
                <EmptyState>Нет медленных запросов</EmptyState>
              ) : (
                slowQueries.map((q) => <SlowQueryCard key={q.pid} query={q} />)
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="daily">
          <DailyReportTab
            report={dailyReport}
            reportDate={reportDate}
            setReportDate={setReportDate}
            loading={loading.report}
            onFetch={(d) =>
              apiRequests.loggerinfo
                .getDailyReport(username, password, d)
                .then(setDailyReport)
                .catch(() => toast.error("Отчёт не найден"))
            }
          />
        </TabsContent>

        <TabsContent value="log">
          <Card>
            <CardHeader>
              <CardTitle>Лог медленных запросов (&gt;2с)</CardTitle>
            </CardHeader>
            <CardContent>
              {loading.log ? (
                <Skeleton className="h-96" />
              ) : (
                <SlowLogTable data={slowRequestsLog} />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog
        open={!!selectedError}
        onOpenChange={() => setSelectedError(null)}
      >
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Детали ошибки</DialogTitle>
          </DialogHeader>
          {selectedError && <ErrorDetail error={selectedError} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Форма входа ----------
const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await apiRequests.loggerinfo.getStatus(username, password);
      onLogin(username, password);
    } catch (err) {
      console.error("Login error:", err);
      if (err.message === "Unauthorized") {
        setError("Неверный логин или пароль");
      } else {
        setError(`Ошибка соединения: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl flex items-center justify-center gap-2">
            <Activity className="h-6 w-6 text-blue-600" />
            TableCRM Monitor
          </CardTitle>
          <CardDescription>Введите учётные данные наблюдателя</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username">Логин</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Пароль</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {error}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Проверка..." : "Войти"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default function LoggerInfoPage() {
  const [credentials, setCredentials] = useState(null);
  if (!credentials)
    return (
      <LoginForm
        onLogin={(u, p) => setCredentials({ username: u, password: p })}
      />
    );
  return (
    <Dashboard
      username={credentials.username}
      password={credentials.password}
    />
  );
}
